import { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    Grid,
    Paper,
    TextField,
    Button,
    IconButton,
    InputAdornment,
    Stack,
    Avatar,
    MenuItem,
    Select,
    CircularProgress,
    Skeleton,
    Chip
} from '@mui/material';
import {
    ArrowBackIosNewOutlined,
    AccountBalanceWalletOutlined,
    PeopleAltOutlined,
    AtmOutlined,
    CheckCircleRounded,
    CalendarMonthOutlined,
    EastOutlined,
    TrendingUpOutlined,
    GridViewRounded,
    // AddOutlined,
    // EditOutlined,
    DeleteOutline
} from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { motion } from 'framer-motion';
import { globalNotifySuccess } from 'views/utilities/AlertToast';
import { setTargetApi, listTargetApi, kpiListApi } from 'utils/API/Mis Apis/HrPortelApis';
import { useCallback } from 'react';
import { useEffect } from 'react';
import { handleCatchError } from 'utils/CatchHandler';
import AddKpiModal from './components/AddKpiModal';
import { useMemo } from 'react';

// ─── Constants ───────────────────────────────────────────────────
const EMPTY_OBJ = {};

const FINANCIAL_YEARS = Array.from({ length: 14 }, (_, i) => {
    const startYear = 2025 + i;
    const endYear = startYear + 1;
    return {
        value: `${startYear}-${endYear}`,
        label: `${startYear}-${endYear}`
    };
});

const INITIAL_TARGET_STATE = {
    financial_year: FINANCIAL_YEARS[0].value,
    increment_percent: 0,
    targets: {} // Structure: { [kpiId]: { [roleId]: value } }
};

const ROLES = [
    { id: 'NH', label: 'National Head' },
    { id: 'ZH', label: 'Zone Head' },
    { id: 'SH', label: 'State Head' },
    { id: 'TL', label: 'Team Leader' },
    { id: 'FL', label: 'Frontline' }
];

const KPI_CONFIG = {
    BC: {
        icon: <PeopleAltOutlined />,
        color: '#14b8a6',
        type: 'Number'
    },
    GTV: {
        icon: <AccountBalanceWalletOutlined />,
        color: '#6366f1',
        type: 'Amount'
    },
    MATM: {
        icon: <AtmOutlined />,
        color: '#ec4899',
        type: 'Number'
    }
};

const DEFAULT_KPI_CONFIG = {
    icon: <GridViewRounded />,
    color: '#64748b',
    type: 'Number'
};

// ─── Components ────────────────────────────────────────────────────────────────

const RoleChip = ({ role, isSelected, onToggle }) => (
    <Box
        onClick={onToggle}
        sx={{
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            px: 2,
            py: 0.6,
            borderRadius: 2,
            border: '1px solid',
            borderColor: isSelected ? 'primary.main' : 'divider',
            bgcolor: isSelected ? 'primary.main' : '#fff',
            color: isSelected ? '#fff' : 'text.secondary',
            transition: 'all 0.2s',
            '&:hover': {
                borderColor: 'primary.main',
                bgcolor: isSelected ? 'primary.main' : 'rgba(25, 118, 210, 0.04)'
            }
        }}
    >
        <Typography variant="caption" fontWeight={isSelected ? 700 : 500}>
            {role.label}
        </Typography>
    </Box>
);

RoleChip.propTypes = {
    role: PropTypes.shape({ label: PropTypes.string }).isRequired,
    isSelected: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired
};

const fetchKpiList = async () => {
    try {
        const response = await kpiListApi();
        if (response?.data?.success) {
            return response.data.data;
        }
        return [];
    } catch (error) {
        console.error('Fetch KPI list error:', error);
        handleCatchError(error);
        return [];
    }
};
const TargetCard = ({ parameter, data, existingData, onChange, globalRole }) => {
    const [localActiveRole, setLocalActiveRole] = useState(globalRole || '');

    useEffect(() => {
        if (globalRole) {
            setLocalActiveRole(globalRole);
        } else if (existingData && Object.keys(existingData).length > 0) {
            // Default to the first role that has existing data
            const firstExisting = Object.keys(existingData)[0];
            setLocalActiveRole(firstExisting);
        } else if (!localActiveRole) {
            // Only reset if no local role is currently selected
            setLocalActiveRole('');
        }
    }, [globalRole, existingData, localActiveRole]);

    const handleValueChange = (e) => {
        let val = e.target.value.replace(/[^0-9.]/g, '');
        // Remove leading zeros but keep "0" if it's the only character or followed by a dot
        if (val.length > 1 && val.startsWith('0') && val[1] !== '.') {
            val = val.substring(1);
        }
        onChange(parameter.id, localActiveRole, val === '' ? 0 : val);
    };

    const existingEntry = existingData?.[localActiveRole];
    const isReadOnly = !!existingEntry;
    const hasAnyExisting = !!existingData && Object.keys(existingData).length > 0;

    const currentValue = isReadOnly
        ? existingEntry.base_target
        : data?.[localActiveRole] === 0 || !data?.[localActiveRole]
        ? ''
        : data[localActiveRole];

    return (
        <motion.div layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: '#fff',
                    height: '100%',
                    position: 'relative',
                    '&:hover': { boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }
                }}
            >
                {/* <IconButton
                    size="small"
                    onClick={() => onEdit(parameter.rawKpi)}
                    sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        color: 'text.secondary',
                        '&:hover': { color: 'primary.main', bgcolor: 'primary.50' }
                    }}
                >
                    <EditOutlined fontSize="small" />
                </IconButton> */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Avatar sx={{ bgcolor: `${parameter.color}10`, color: parameter.color, width: 44, height: 44 }}>
                        {parameter.icon}
                    </Avatar>
                    <Box>
                        <Typography variant="subtitle1" fontWeight={700} color="text.primary" lineHeight={1.2}>
                            {parameter.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {parameter.description}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                    {ROLES.map((role) => {
                        const hasValue = data?.[role.id] && data[role.id] > 0;
                        const isExisting = !!existingData?.[role.id];
                        const isSelected = localActiveRole === role.id;
                        return (
                            <Box
                                key={role.id}
                                onClick={() => {
                                    if (!hasAnyExisting) {
                                        setLocalActiveRole(role.id);
                                        // Move current value to the new role instead of clearing it
                                        onChange(parameter.id, role.id, currentValue === '' ? 0 : parseInt(currentValue, 10));
                                    }
                                }}
                                sx={{
                                    cursor: hasAnyExisting ? 'default' : 'pointer',
                                    px: 1.2,
                                    py: 0.4,
                                    borderRadius: 1.5,
                                    border: '1px solid',
                                    borderColor: isSelected
                                        ? 'primary.main'
                                        : isExisting
                                        ? 'success.main'
                                        : hasValue
                                        ? 'primary.light'
                                        : 'divider',
                                    bgcolor: isSelected ? 'primary.main' : isExisting ? 'success.50' : hasValue ? 'primary.50' : '#fff',
                                    color: isSelected ? '#fff' : isExisting ? 'success.main' : hasValue ? 'primary.main' : 'text.secondary',
                                    transition: 'all 0.2s',
                                    fontSize: '0.65rem',
                                    fontWeight: isSelected || hasValue || isExisting ? 700 : 500,
                                    opacity: hasAnyExisting && !isSelected ? 0.5 : 1
                                }}
                            >
                                {role.id}
                            </Box>
                        );
                    })}
                </Box>

                <TextField
                    fullWidth
                    size="small"
                    label={isReadOnly ? `${parameter.label} Target (Existing)` : `Target ${parameter.type}`}
                    value={currentValue}
                    placeholder={localActiveRole ? 'Set Target' : 'Select Role First'}
                    onChange={handleValueChange}
                    disabled={isReadOnly}
                    onBlur={(e) => {
                        if (e.target.value === '') onChange(parameter.id, localActiveRole, 0);
                    }}
                    InputProps={{
                        startAdornment: parameter.type === 'Amount' ? <InputAdornment position="start">₹</InputAdornment> : null,
                        endAdornment: isReadOnly ? (
                            <InputAdornment position="end">
                                <Chip
                                    label={`+${existingEntry.increment_percent}%`}
                                    size="small"
                                    variant="outlined"
                                    color="success"
                                    sx={{ height: 20, fontSize: '0.65rem' }}
                                />
                                <CheckCircleRounded sx={{ ml: 1, fontSize: 16, color: 'success.main' }} />
                            </InputAdornment>
                        ) : parameter.type === 'Percentage' ? (
                            <InputAdornment position="end">%</InputAdornment>
                        ) : null,
                        sx: {
                            fontWeight: 700,
                            borderRadius: 2,
                            bgcolor: isReadOnly ? 'success.50' : '#f8fafc',
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: isReadOnly ? 'success.light' : 'divider'
                            }
                        }
                    }}
                />
            </Paper>
        </motion.div>
    );
};

TargetCard.propTypes = {
    parameter: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string,
        type: PropTypes.string,
        icon: PropTypes.node,
        color: PropTypes.string,
        description: PropTypes.string,
        rawKpi: PropTypes.object
    }).isRequired,
    data: PropTypes.object,
    existingData: PropTypes.object,
    onChange: PropTypes.func.isRequired,
    onEdit: PropTypes.func.isRequired,
    globalRole: PropTypes.string
};

const TargetSettings = () => {
    const navigate = useNavigate();
    const [targetData, setTargetData] = useState(INITIAL_TARGET_STATE);
    const [isSaving, setIsSaving] = useState(false);
    const [targetsList, setTargetsList] = useState({});
    // const [listLoading, setListLoading] = useState(true);
    const [kpiList, setKpiList] = useState([]);
    const [kpisLoading, setKpisLoading] = useState(true);
    const [openKpiModal, setOpenKpiModal] = useState(false);
    const [editingKpi, setEditingKpi] = useState(null);
    const [isDirty, setIsDirty] = useState(false);

    // const groupedTargets = useMemo(() => {
    //     const groups = {};
    //     Object.values(targetsList || {}).forEach((item) => {
    //         const roleKey = item.role;
    //         if (!groups[roleKey]) {
    //             groups[roleKey] = {
    //                 role: item.role,
    //                 financial_year: item.financial_year,
    //                 kpis: []
    //             };
    //         }
    //         groups[roleKey].kpis.push(item);
    //     });
    //     return Object.values(groups);
    // }, [targetsList]);

    const existingTargetsMap = useMemo(() => {
        const map = {};
        const API_TO_UI_ROLE_MAP = {
            manager: 'FL',
            middle_manager: 'TL'
        };

        Object.values(targetsList || {}).forEach((item) => {
            const kpiCode = item.kpi_code;
            const apiRole = item.role?.toLowerCase()?.replace(/\s/g, '_') || '';

            // 1. Try explicit mapping first
            let roleShort = API_TO_UI_ROLE_MAP[apiRole];

            // 2. Fall back to standard label/ID matching
            if (!roleShort) {
                roleShort =
                    ROLES.find((r) => {
                        const configRoleRef = r.label?.toLowerCase()?.replace(/\s/g, '_') || '';
                        return apiRole === configRoleRef || apiRole === r.id?.toLowerCase();
                    })?.id || item.role;
            }

            if (kpiCode) {
                if (!map[kpiCode]) map[kpiCode] = {};
                map[kpiCode][roleShort] = item;
            }
        });
        return map;
    }, [targetsList]);

    const fetchKpis = useCallback(async () => {
        setKpisLoading(true);
        const data = await fetchKpiList();
        setKpiList(data);
        setKpisLoading(false);
    }, []);

    const fetchTargets = useCallback(async (year) => {
        if (!year) return;
        // setListLoading(true);
        try {
            const response = await listTargetApi({ financial_year: year });
            if (response?.data?.success) {
                setTargetsList(response.data.data);
            }
        } catch (error) {
            console.error('Fetch targets error:', error);
        } finally {
            // setListLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTargets(targetData.financial_year);
        fetchKpis();
    }, [fetchTargets, fetchKpis, targetData.financial_year]);

    const handleTargetChange = (paramId, field, value) => {
        setIsDirty(true);
        if (field === 'global') {
            let finalValue = value;
            if (paramId === 'increment_percent') {
                const num = parseFloat(value);
                if (!isNaN(num) && num < 0) finalValue = 0;
            }
            setTargetData((prev) => ({ ...prev, [paramId]: finalValue }));
            return;
        }
        // role-based update
        setTargetData((prev) => {
            // Enforce single role per KPI by always creating a new object for that KPI
            const newKpiTargets = { [field]: value };

            return {
                ...prev,
                targets: {
                    ...(prev.targets || {}),
                    [paramId]: newKpiTargets
                }
            };
        });
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const kpiEntries = [];

            Object.entries(targetData.targets || {}).forEach(([kpiId, roleValues]) => {
                const kpi = kpiList.find((k) => k.id.toString() === kpiId.toString());
                if (!kpi) return;

                Object.entries(roleValues).forEach(([role, val]) => {
                    const parsedVal = parseFloat(val);
                    if (parsedVal > 0) {
                        kpiEntries.push({
                            kpi_code: kpi.code,
                            role: role,
                            base_target: parsedVal
                        });
                    }
                });
            });

            if (kpiEntries.length === 0) return;

            const payload = {
                financial_year: targetData.financial_year,
                increment_percent: parseFloat(targetData.increment_percent) || 0,
                kpis: kpiEntries
            };

            const response = await setTargetApi(payload);
            if (response?.data?.success) {
                globalNotifySuccess('Target benchmarks saved successfully!');
                setTargetData(INITIAL_TARGET_STATE);
                setIsDirty(false);
                fetchTargets(targetData.financial_year); // Refresh the list
            }
        } catch (error) {
            console.error('Save error:', error);
        } finally {
            setIsSaving(false);
        }
    };

    // const handleDeleteTarget = async (id) => {
    //     if (!window.confirm('Are you sure you want to delete this target benchmark?')) return;
    //     try {
    //         const response = await deleteTargetApi({ id });
    //         if (response?.data?.success) {
    //             globalNotifySuccess('Target benchmark deleted successfully!');
    //             fetchTargets(targetData.financial_year);
    //         }
    //     } catch (error) {
    //         console.error('Delete target error:', error);
    //         handleCatchError(error);
    //     }
    // };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', pb: 6 }}>
            {/* ── Compact Header ── */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', // Vibrant Enterprise Blue
                    px: { xs: 2, md: 4 },
                    pt: 3,
                    pb: 6,
                    borderRadius: '10px'
                }}
            >
                <IconButton
                    onClick={() => navigate('/HR/dashboard')}
                    sx={{ color: '#fff', mb: 1, bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
                    size="small"
                >
                    <ArrowBackIosNewOutlined fontSize="inherit" />
                </IconButton>
                {/* Decorative circles */}
                {[
                    { size: 200, top: -80, right: -60, op: 0.07 },
                    { size: 120, top: 10, right: 120, op: 0.05 }
                ].map((c, i) => (
                    <Box
                        key={i}
                        sx={{
                            position: 'absolute',
                            top: c.top,
                            right: c.right,
                            width: c.size,
                            height: c.size,
                            borderRadius: '50%',
                            bgcolor: '#fff',
                            opacity: c.op
                        }}
                    />
                ))}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                    <Box>
                        <Typography variant="h4" fontWeight={800} sx={{ color: '#fff' }}>
                            Target Benchmarks
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                            Set performance benchmarks for organizational roles
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="outlined"
                            startIcon={<GridViewRounded />}
                            onClick={() => navigate('/HR/target-matrix')}
                            sx={{
                                color: '#fff',
                                borderColor: 'rgba(255,255,255,0.4)',
                                px: 3,
                                py: 1.2,
                                borderRadius: 2,
                                fontWeight: 700,
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.1)',
                                    borderColor: '#fff'
                                }
                            }}
                        >
                            Target Matrix
                        </Button>
                        {/* <Button
                            variant="contained"
                            startIcon={<AddOutlined />}
                            onClick={() => {
                                setEditingKpi(null);
                                setOpenKpiModal(true);
                            }}
                            sx={{
                                bgcolor: 'rgba(255,255,255,0.15)',
                                color: '#fff',
                                px: 3,
                                py: 1.2,
                                borderRadius: 2,
                                fontWeight: 700,
                                backdropFilter: 'blur(4px)',
                                border: '1px solid rgba(255,255,255,0.2)',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
                            }}
                        >
                            Add KPI
                        </Button> */}
                        <Button
                            variant="contained"
                            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <CheckCircleRounded />}
                            disabled={!isDirty || isSaving}
                            onClick={handleSave}
                            sx={{
                                bgcolor: '#fff',
                                color: '#1e3a8a',
                                px: 4,
                                py: 1.2,
                                borderRadius: 2,
                                fontWeight: 700,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                '&:hover': { bgcolor: '#f8fafc' },
                                '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.4)' }
                            }}
                        >
                            Save Targets
                        </Button>
                    </Stack>
                </Box>
            </Box>

            {/* ── Sleek Configuration Console ── */}
            <Box sx={{ px: { xs: 2, md: 4 }, mt: -4 }}>
                <Paper
                    elevation={4}
                    sx={{
                        p: 2,
                        borderRadius: 3,
                        bgcolor: '#fff',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 12px 24px rgba(0,0,0,0.05)'
                    }}
                >
                    <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} lg={8}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <CalendarMonthOutlined color="primary" fontSize="small" />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">
                                        DURATION (Financial Year)
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        <Select
                                            size="small"
                                            value={targetData.financial_year}
                                            onChange={(e) => handleTargetChange('financial_year', 'global', e.target.value)}
                                            sx={{ minWidth: 130, fontSize: '0.8rem' }}
                                        >
                                            {FINANCIAL_YEARS.map((year) => (
                                                <MenuItem key={year.value} value={year.value} sx={{ fontSize: '0.8rem' }}>
                                                    {year.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <EastOutlined sx={{ color: 'divider' }} fontSize="inherit" />
                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                            Apr - Mar
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>

                        <Grid item xs={12} lg={4}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    borderLeft: { lg: '1px solid' },
                                    borderColor: 'divider',
                                    pl: { lg: 3 }
                                }}
                            >
                                <TrendingUpOutlined color="primary" fontSize="small" />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary" display="block">
                                        INCREMENT %
                                    </Typography>
                                    <TextField
                                        size="small"
                                        value={targetData.increment_percent}
                                        onChange={(e) => {
                                            let val = e.target.value.replace(/[^0-9.]/g, '');
                                            // Prevent multiple decimal points
                                            const parts = val.split('.');
                                            if (parts.length > 2) {
                                                val = parts[0] + '.' + parts.slice(1).join('');
                                            }
                                            if (val.length > 1 && val.startsWith('0') && val[1] !== '.') {
                                                val = val.substring(1);
                                            }
                                            handleTargetChange('increment_percent', 'global', val);
                                        }}
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                            sx: { fontSize: '0.85rem', fontWeight: 700 }
                                        }}
                                        inputProps={{ step: 'any' }}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>

                <Stack spacing={3} sx={{ mt: 3 }}>
                    <Grid container spacing={3}>
                        {kpisLoading ? (
                            [1, 2, 3].map((i) => (
                                <Grid item xs={12} md={6} lg={4} key={i}>
                                    <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 4 }} />
                                </Grid>
                            ))
                        ) : kpiList.length > 0 ? (
                            kpiList.map((kpi) => {
                                const config = KPI_CONFIG[kpi.code] || DEFAULT_KPI_CONFIG;
                                const parameter = {
                                    id: kpi.id,
                                    label: kpi.name,
                                    type: config.type,
                                    icon: config.icon,
                                    color: config.color,
                                    description: kpi.description,
                                    rawKpi: kpi
                                };
                                return (
                                    <Grid item xs={12} md={6} lg={4} key={kpi.id}>
                                        <TargetCard
                                            parameter={parameter}
                                            data={targetData.targets?.[kpi.id] || EMPTY_OBJ}
                                            existingData={existingTargetsMap[kpi.code] || EMPTY_OBJ}
                                            onChange={handleTargetChange}
                                            onEdit={(kpi) => {
                                                setEditingKpi(kpi);
                                                setOpenKpiModal(true);
                                            }}
                                        />
                                    </Grid>
                                );
                            })
                        ) : (
                            <Grid item xs={12}>
                                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: '1px dashed', borderColor: 'divider' }}>
                                    <Typography color="text.secondary">No KPIs available to configure.</Typography>
                                </Paper>
                            </Grid>
                        )}
                    </Grid>
                </Stack>

                {/* ── Targets List Section ── */}
                {/* <Box sx={{ mt: 8 }}>
                    <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography variant="h5" fontWeight={800} color="text.primary">
                                Existing Target Benchmarks
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Review previously set performance metrics for various roles
                            </Typography>
                        </Box>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => fetchTargets(targetData.financial_year)}
                            disabled={listLoading}
                            startIcon={<TrendingUpOutlined />}
                        >
                            Refresh List
                        </Button>
                    </Box>

                    {listLoading ? (
                        <Grid container spacing={3}>
                            {[1, 2, 3].map((i) => (
                                <Grid item xs={12} key={i}>
                                    <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 3 }} />
                                </Grid>
                            ))}
                        </Grid>
                    ) : groupedTargets.length > 0 ? (
                        <Stack spacing={3}>
                            {groupedTargets.map((group) => (
                                <TargetListItem key={group.role} group={group} onDelete={handleDeleteTarget} />
                            ))}
                        </Stack>
                    ) : (
                        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: '1px dashed', borderColor: 'divider' }}>
                            <Typography color="text.secondary">No targets set yet.</Typography>
                        </Paper>
                    )}
                </Box> */}
            </Box>
            <AddKpiModal
                open={openKpiModal}
                handleClose={() => {
                    setOpenKpiModal(false);
                    setEditingKpi(null);
                }}
                onRefresh={fetchKpis}
                editKpi={editingKpi}
            />
        </Box>
    );
};

const TargetListItem = ({ group, onDelete }) => {
    return (
        <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}>
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: '#fff',
                    transition: 'all 0.2s',
                    '&:hover': {
                        borderColor: 'primary.light',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.04)'
                    }
                }}
            >
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={3}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar sx={{ bgcolor: 'secondary.main', width: 48, height: 48 }}>
                                <CalendarMonthOutlined />
                            </Avatar>
                            <Box>
                                <Typography variant="subtitle2" fontWeight={800} color="text.primary">
                                    FY {group.financial_year}
                                </Typography>
                                <Chip
                                    label={group.role.replace(/_/g, ' ')}
                                    size="small"
                                    sx={{
                                        mt: 0.5,
                                        textTransform: 'uppercase',
                                        fontSize: '0.65rem',
                                        fontWeight: 900,
                                        bgcolor: 'primary.50',
                                        color: 'primary.main',
                                        height: 20
                                    }}
                                />
                            </Box>
                        </Stack>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <Grid container spacing={2}>
                            {group.kpis.map((kpi, idx) => (
                                <Grid item xs={6} sm={4} lg={3} key={idx}>
                                    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid', borderColor: 'divider' }}>
                                        <Typography
                                            variant="caption"
                                            color="primary"
                                            fontWeight={800}
                                            sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                                        >
                                            {kpi.kpi_name}
                                        </Typography>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                            <Typography variant="body2" fontWeight={900}>
                                                {kpi.kpi_name.toLowerCase().includes('gtv') || kpi.kpi_name.toLowerCase().includes('amount')
                                                    ? `₹${parseFloat(kpi.base_target).toLocaleString()}`
                                                    : parseFloat(kpi.base_target)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" fontWeight={700}>
                                                +{kpi.increment_percent}%
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={1}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            {group.kpis.map(
                                (kpi) =>
                                    kpi.delete_possible && (
                                        <IconButton
                                            key={kpi.id}
                                            size="small"
                                            color="error"
                                            onClick={() => onDelete(kpi.id)}
                                            title={`Delete ${kpi.kpi_name}`}
                                            sx={{
                                                bgcolor: 'error.50',
                                                '&:hover': { bgcolor: 'error.100' }
                                            }}
                                        >
                                            <DeleteOutline fontSize="small" />
                                        </IconButton>
                                    )
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Paper>
        </motion.div>
    );
};

TargetListItem.propTypes = {
    group: PropTypes.shape({
        financial_year: PropTypes.string,
        role: PropTypes.string,
        kpis: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number,
                kpi_name: PropTypes.string,
                base_target: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                increment_percent: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            })
        )
    }).isRequired,
    onDelete: PropTypes.func.isRequired
};

export default TargetSettings;
